import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../../shared/stores/RootStore";
import { t } from "i18next";
import { defaultNodeScopes } from "../../cra/stores/CraNodeStore";

const i18prefix = "map:loading";

export enum FetchingStatus {
  NotStarted = "NotStarted",
  Fetching = "Fetching",
  Done = "Done",
  Error = "Error",
}

enum FetchingStep {
  SurveysList = "surveysList",
  Survey = "survey",
  SurveyDefaults = "surveyDefaults",
  CraWays = "craWays",
  HlaWays = "hlaWays",
  SimulatedHlaWays = "simulatedHlaWays",
  Dashboards = "dashboards",
  CraForecastScenarios = "craForecastScenarios",
  HlaForecastScenarios = "hlaForecastScenarios",
  NodesAndMetadata = "nodesAndMetadata",
  OsmData = "osmData",
}

type FetchingState = Record<FetchingStep, FetchingStatus>;

// TODO manage error and retry logic
export class LoadingStore {
  rootStore;

  // used to cancel running loading processes when the context changes
  latestScope = "Home";

  // natural language text to be shown on the screen
  message: string | undefined;

  fetchingState: FetchingState = {
    surveysList: FetchingStatus.NotStarted,
    survey: FetchingStatus.NotStarted,
    surveyDefaults: FetchingStatus.NotStarted,
    craWays: FetchingStatus.NotStarted,
    hlaWays: FetchingStatus.NotStarted,
    simulatedHlaWays: FetchingStatus.NotStarted,
    dashboards: FetchingStatus.NotStarted,
    craForecastScenarios: FetchingStatus.NotStarted,
    hlaForecastScenarios: FetchingStatus.NotStarted,
    nodesAndMetadata: FetchingStatus.NotStarted,
    osmData: FetchingStatus.NotStarted,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  registerScope(scope: string) {
    runInAction(() => {
      this.latestScope = scope;
    });
  }

  isScopeActive(scope: string) {
    return this.latestScope === scope;
  }

  onSurveySelected() {
    this.setFetchingStatus({
      craWays: FetchingStatus.NotStarted,
      hlaWays: FetchingStatus.NotStarted,
      dashboards: FetchingStatus.NotStarted,
      craForecastScenarios: FetchingStatus.NotStarted,
      hlaForecastScenarios: FetchingStatus.NotStarted,
      simulatedHlaWays: FetchingStatus.NotStarted,
      nodesAndMetadata: FetchingStatus.NotStarted,
    });
  }

  setFetchingStatus(data: Partial<FetchingState>) {
    runInAction(() => {
      this.fetchingState = { ...this.fetchingState, ...data };
    });
  }

  private setMessage(message: string | undefined) {
    runInAction(() => {
      this.message = message;
    });
  }

  async loadSurveysList(scope: string) {
    await this.runStep(FetchingStep.SurveysList, scope, async (cancelIf) => {
      const { surveyStore } = this.rootStore;
      return surveyStore.fetchAllSurveys(cancelIf);
    });
  }

  async loadSurvey(surveyId: string, scope: string) {
    await this.runStep(FetchingStep.Survey, scope, async (cancelIf) => {
      const { surveyStore } = this.rootStore;
      surveyStore.select(surveyId);
      return surveyStore.fetchSurvey(surveyId, cancelIf);
    });
  }

  async loadSurveyDefaults(surveyId: string, scope: string) {
    await this.runStep(FetchingStep.SurveyDefaults, scope, async (cancelIf) => {
      const { surveyStore } = this.rootStore;
      return surveyStore.fetchSurveyDefaults(surveyId, cancelIf);
    });
  }

  async loadCraForecastScenarios(surveyId: string, scope: string) {
    await this.runStep(
      FetchingStep.CraForecastScenarios,
      scope,
      async (cancelIf) => {
        const { craForecastStore } = this.rootStore;
        return craForecastStore.fetchScenarios(surveyId, cancelIf);
      },
    );
  }

  async loadHlaForecastScenarios(surveyId: string, scope: string) {
    await this.runStep(
      FetchingStep.HlaForecastScenarios,
      scope,
      async (cancelIf) => {
        const { hlaForecastStore } = this.rootStore;
        return hlaForecastStore.fetchScenarios(surveyId, cancelIf);
      },
    );
  }

  async loadDashboards(surveyId: string, scope: string) {
    await this.runStep(FetchingStep.Dashboards, scope, async (cancelIf) => {
      const { dashboardStore } = this.rootStore;
      return dashboardStore.fetchDashboards(surveyId, cancelIf);
    });
  }

  async loadCraWays(surveyId: string, scope: string) {
    await this.runStep(FetchingStep.CraWays, scope, async (cancelIf) => {
      const { craWayStore } = this.rootStore;
      return craWayStore.fetchAllWays(surveyId, cancelIf);
    });
  }

  async loadCraNodesAndMetadata(scope?: string) {
    const _scope = scope ?? this.latestScope;

    await this.runStep(
      FetchingStep.NodesAndMetadata,
      _scope,
      async (cancelIf) => {
        const { craNodeStore, craCommentStore, craWayStore, codingStore } =
          this.rootStore;

        for (const way of craWayStore.ways) {
          this.setMessage(t(`${i18prefix}.nodes`, { name: way.name }));

          await craNodeStore.fetchNodes(way.id, defaultNodeScopes, cancelIf);

          this.setMessage(t(`${i18prefix}.coding`, { name: way.name }));

          await codingStore.fetchAttributesByCraWayId(way.id, cancelIf);

          this.setMessage(t(`${i18prefix}.comments`, { name: way.name }));

          if (way.stats!.nActiveComments && way.permissions.canComment) {
            await craCommentStore.fetchComments(way.id, cancelIf);
          }
        }
      },
    );
  }

  async loadHlaWays(surveyId: string, scope: string) {
    await this.runStep(FetchingStep.HlaWays, scope, async (cancelIf) => {
      const { hlaStore } = this.rootStore;
      return hlaStore.fetchAll(surveyId, cancelIf);
    });
  }

  async loadSimulatedHlaWayIds(surveyId: string, scope: string) {
    await this.runStep(
      FetchingStep.SimulatedHlaWays,
      scope,
      async (cancelIf) => {
        const { simulationStore, mapStore } = this.rootStore;
        await simulationStore.loadSimulatedHlaWayIds(surveyId);
        if (!cancelIf()) {
          mapStore.hlaMapSource?.onStoreUpdate();
        }
      },
    );
  }

  async loadOsmData(cityId: string, scope: string) {
    await this.runStep(FetchingStep.OsmData, scope, async (cancelIf) => {
      const { osmToolsStore } = this.rootStore;
      return osmToolsStore.fetchData(cityId, cancelIf);
    });
  }

  async runStep(
    step: FetchingStep,
    scope: string,
    fn: (cancelIf: () => boolean) => Promise<void>,
  ) {
    if (this.isStepSkippable(step)) return;
    this.announceStep(step);
    return await this.awaitStep(
      step,
      fn(() => this.cancelIfScopeChanged(step, scope)),
    );
  }

  private cancelIfScopeChanged(step: FetchingStep, scope: string) {
    const scopeChanged = !this.isScopeActive(scope);
    if (scopeChanged) {
      console.log(`Step "${step}" with scope "${scope}" was cancelled.`);
    }
    return scopeChanged;
  }

  private isStepSkippable(step: FetchingStep) {
    if (
      this.fetchingState[step] === FetchingStatus.Done ||
      this.fetchingState[step] === FetchingStatus.Fetching
    ) {
      console.log(
        `Skipping loading step "${step}" as its status is ${this.fetchingState[step]}`,
      );
      return true;
    }
    return false;
  }

  private async announceStep(step: FetchingStep) {
    console.log(`Starting loading step "${step}"`);
    this.setMessage(t(`${i18prefix}.${step}`));
    this.setFetchingStatus({
      [step]: FetchingStatus.Fetching,
    });
  }

  private async awaitStep<T>(step: FetchingStep, promise: Promise<T>) {
    await promise
      .then(() => {
        this.setFetchingStatus({
          [step]: FetchingStatus.Done,
        });
      })
      .catch((e) => {
        this.setFetchingStatus({
          [step]: FetchingStatus.Error,
        });
        console.log(`Failed to load ${step}`);
        console.error(e);

        throw e;
      })
      .finally(() => {
        this.setMessage(undefined);
      });
  }
}
